import { logger } from '@/utils/logger';
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { videosApi, Video, ROIPolygon, ReferenceLine } from '@/api/upload/videos';
import { generateFlowVectors } from '@/utils/upload/uploadUtils';
import { useROI } from '@/contexts/useROI';
import { FlowVector } from '@/components/shared/types';

export interface AnalysisResults {
    averageVelocity: number;
    maxVelocity: number;
    minVelocity: number;
    flowDirection?: number;
    analyzedFrames?: number;
    potentialPower?: number;
}

interface AnalysisContextType {
    // Video state
    selectedVideo: Video | null;
    setSelectedVideo: (video: Video | null) => void;

    // Analysis state
    isAnalyzing: boolean;
    setIsAnalyzing: (analyzing: boolean) => void;
    analysisResults: AnalysisResults | null;
    setAnalysisResults: (results: AnalysisResults | null) => void;

    // Processing state
    processingProgress: number;
    processingStatus: string;
    processingError: string | null;

    // Flow visualization
    showVectors: boolean;
    setShowVectors: (show: boolean) => void;
    flowVectorsData: FlowVector[];
    setFlowVectorsData: (data: FlowVector[]) => void;

    // Analysis actions
    startAnalysis: (params: {
        selectedVideo: Video | null;
        referencePoints: { x: number, y: number }[];
        referenceLengthMeters: number;
        polygonPoints: { x: number, y: number }[];
        isPolygonClosed: boolean;
    }) => Promise<void>;
    cancelAnalysis: () => Promise<void>;
}

const AnalysisContext = createContext<AnalysisContextType | undefined>(undefined);

// Export the context for the hook file
export { AnalysisContext };
export type { AnalysisContextType };

interface AnalysisProviderProps {
    children: ReactNode;
    imageRef: React.RefObject<HTMLImageElement>;
}

const isValidUUID = (id: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
};

export const AnalysisProvider: React.FC<AnalysisProviderProps> = ({ children, imageRef }) => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { resetAll } = useROI();
    const {
        channelDepth,
        currentPowerProduction,
        siteName,
        coordinates,
        latitude,
        longitude,
        headHeight,
        yearBuilt,
        numberOfGates,
        numberOfSections,
        csvSiteData,
        calibrationMode,
        calibrationResult,
    } = useROI();

    // Core state
    const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);

    // Processing state
    const [processingProgress, setProcessingProgress] = useState(0);
    const [processingStatus, setProcessingStatus] = useState<string>('');
    const [processingError, setProcessingError] = useState<string | null>(null);

    // Visualization state
    const [showVectors, setShowVectors] = useState(false);
    const [flowVectorsData, setFlowVectorsData] = useState<FlowVector[]>([]);

    const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isPollingCancelledRef = useRef(false);

    const stopPolling = () => {
        isPollingCancelledRef.current = true;
        if (pollTimeoutRef.current !== null) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, []);

    // Cancel analysis function
    const cancelAnalysis = async () => {
        if (!selectedVideo) {
            logger.error("Cannot cancel analysis: no video selected");
            return;
        }
        if (!selectedVideo.uid || !isValidUUID(selectedVideo.uid)) {
            logger.error(`Invalid video ID format: "${selectedVideo.uid}". Must be a valid UUID.`);
            toast({
                variant: "destructive",
                title: "Error canceling analysis",
                description: "Invalid video ID format. Please try refreshing the page.",
            });
            return;
        }


        stopPolling();

        try {
            await videosApi.cancelProcessing(selectedVideo.uid);
            setIsAnalyzing(false);
            setProcessingProgress(0);
            setProcessingStatus('');
            setProcessingError(null);
            resetAll();

            setSelectedVideo({ ...selectedVideo, status: 'canceled' });

            toast({
                title: "Analysis cancelled",
                description: "The analysis process has been cancelled successfully.",
            });
        } catch (error) {
            let errorMessage = "Failed to cancel the analysis process.";
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            }

            toast({
                variant: "destructive",
                title: "Cancellation failed",
                description: errorMessage,
            });

            setIsAnalyzing(false);
            setProcessingProgress(0);
            setProcessingStatus('');
        }
    };

    const startAnalysis = async ({
        selectedVideo,
        referencePoints,
        referenceLengthMeters,
        polygonPoints,
        isPolygonClosed,
    }) => {
        const usingApriltag = calibrationMode === 'apriltag';

        if (!selectedVideo) {
            toast({
                variant: "destructive",
                title: "Missing video",
                description: "Please select a video before analysis",
            });
            return;
        }

        if (!usingApriltag && referencePoints.length !== 2) {
            toast({
                variant: "destructive",
                title: "Missing information",
                description: "Please define ROI and reference points before analysis",
            });
            return;
        }

        if (usingApriltag && !calibrationResult) {
            toast({
                variant: "destructive",
                title: "Calibration required",
                description: "Run AprilTag auto-calibration or switch to manual reference line.",
            });
            return;
        }

        // Validate the UUID format
        if (!selectedVideo.uid || !isValidUUID(selectedVideo.uid)) {
            logger.error(`Invalid video ID format: "${selectedVideo.uid}". Must be a valid UUID.`);
            toast({
                variant: "destructive",
                title: "Error starting analysis",
                description: "Invalid video ID format. Please try refreshing the page.",
            });
            return;
        }

        if (polygonPoints.length < 3 || !isPolygonClosed) {
            toast({
                variant: "destructive",
                title: "Missing ROI",
                description: "Please draw and complete a polygon ROI before analysis",
            });
            return;
        }

        if (!usingApriltag && !(referenceLengthMeters > 0)) {
            toast({
                variant: "destructive",
                title: "Reference scale required",
                description: "Enter a positive reference length (meters).",
            });
            return;
        }

        setIsAnalyzing(true);
        setProcessingProgress(0);
        setProcessingStatus('Initializing');
        setProcessingError(null);

        try {
            const img = imageRef.current;
            const imgWidth = img?.naturalWidth || 1920;
            const imgHeight = img?.naturalHeight || 1080;

            const roiPolygon = {
                points: polygonPoints.map(point => ({
                    x: Math.round((point.x / 100) * imgWidth),
                    y: Math.round((point.y / 100) * imgHeight)
                }))
            };

            const referenceLine = usingApriltag
                ? null
                : {
                    start: {
                        x: Math.round((referencePoints[0].x / 100) * imgWidth),
                        y: Math.round((referencePoints[0].y / 100) * imgHeight)
                    },
                    end: {
                        x: Math.round((referencePoints[1].x / 100) * imgWidth),
                        y: Math.round((referencePoints[1].y / 100) * imgHeight)
                    },
                    realWorldLength: referenceLengthMeters,
                    unit: "m"
                };

            await videosApi.updateROI(selectedVideo.uid, roiPolygon);
            if (referenceLine) {
                await videosApi.updateReference(selectedVideo.uid, referenceLine);
            }
            await videosApi.startAnalysis(
                selectedVideo.uid,
                roiPolygon,
                referenceLine,
                {},
                channelDepth,
                currentPowerProduction,
                siteName,
                coordinates,
                latitude,
                longitude,
                headHeight,
                yearBuilt,
                numberOfGates,
                numberOfSections,
                csvSiteData
            );

            toast({
                title: "Analysis started",
                description: "Processing video... This may take several minutes.",
            });

            const pollInterval = 5000;
            const maxPolls = 720;
            let pollCount = 0;

            isPollingCancelledRef.current = false;

            const pollAnalysisStatus = async () => {
                if (isPollingCancelledRef.current) {
                    return;
                }

                try {
                    const statusResponse = await videosApi.getProcessingStatus(selectedVideo.uid);

                    let progress = 0;
                    let state = '';
                    let status = '';
                    let error = null;

                    if (statusResponse) {
                        progress = statusResponse.progress || statusResponse.meta?.progress || 0;
                        state = statusResponse.state || '';
                        status = statusResponse.status || statusResponse.meta?.status || '';
                        error = statusResponse.error || statusResponse.meta?.error || null;
                    }

                    if ((state === 'PENDING' || state === 'STARTED' || status === 'processing') && !isAnalyzing) {
                        setIsAnalyzing(true);
                    }

                    setProcessingProgress(progress);
                    setProcessingStatus(status || state || 'Processing');
                    setSelectedVideo({
                        ...selectedVideo,
                        status: status || (state === 'STARTED' ? 'processing' : state === 'SUCCESS' ? 'ready' : state === 'FAILURE' ? 'failed' : 'processing')
                    });

                    if (error) {
                        setProcessingError(error);
                        setIsAnalyzing(false);
                        toast({
                            variant: "destructive",
                            title: "Analysis failed",
                            description: error || "An error occurred during video processing",
                        });
                        return;
                    }

                    if (state === 'SUCCESS' || state === 'FAILURE') {
                        if (state === 'SUCCESS') {
                            const updatedVideo = await videosApi.getAnalysis(selectedVideo.uid);
                            if (updatedVideo && updatedVideo.flow_statistics) {
                                const results = {
                                    averageVelocity: updatedVideo.flow_statistics.average_speed || 0,
                                    maxVelocity: updatedVideo.flow_statistics.max_speed || 0,
                                    minVelocity: updatedVideo.flow_statistics.min_speed || 0,
                                    ...(updatedVideo.flow_statistics.flow_direction && {
                                        flowDirection: updatedVideo.flow_statistics.flow_direction
                                    }),
                                    ...(updatedVideo.flow_statistics.analyzed_frames && {
                                        analyzedFrames: updatedVideo.flow_statistics.analyzed_frames
                                    }),
                                    ...(updatedVideo.flow_statistics.potential_power && {
                                        potentialPower: updatedVideo.flow_statistics.potential_power
                                    })
                                };

                                setAnalysisResults(results);
                                setShowVectors(true);
                                setIsAnalyzing(false);
                                setFlowVectorsData(generateFlowVectors(20, results.maxVelocity));
                                resetAll();

                                toast({
                                    title: "Analysis complete",
                                    description: "Flow analysis has been successfully completed. Redirecting to analysis page...",
                                });

                                setTimeout(() => {
                                    if (!isPollingCancelledRef.current) {
                                        navigate(`/analysis?video=${selectedVideo.uid}`);
                                    }
                                }, 1500);

                                setSelectedVideo(updatedVideo);
                                return;
                            } else {
                                setIsAnalyzing(false);
                                toast({
                                    variant: "destructive",
                                    title: "Incomplete analysis",
                                    description: "The analysis completed but some results are missing",
                                });
                            }
                        } else {
                            setIsAnalyzing(false);
                            toast({
                                variant: "destructive",
                                title: "Analysis failed",
                                description: error || "The video analysis failed to complete",
                            });
                        }
                        return;
                    }

                    if (pollCount >= maxPolls) {
                        setIsAnalyzing(false);
                        toast({
                            variant: "destructive",
                            title: "Analysis timeout",
                            description: "The video analysis is taking longer than expected. Please try again later.",
                        });
                        return;
                    }

                    pollCount++;
                    if (!isPollingCancelledRef.current) {
                        pollTimeoutRef.current = setTimeout(pollAnalysisStatus, pollInterval);
                    }
                } catch (error) {
                    setIsAnalyzing(false);
                    let errorMessage = "An error occurred while checking analysis status";
                    if (error.response?.data?.detail) {
                        errorMessage = error.response.data.detail;
                    } else if (error.message) {
                        errorMessage = error.message;
                    }

                    toast({
                        variant: "destructive",
                        title: "Analysis status check failed",
                        description: errorMessage,
                    });
                }
            };

            pollTimeoutRef.current = setTimeout(pollAnalysisStatus, pollInterval);
        } catch (error) {
            let errorMessage = "An error occurred during analysis.";
            if (error.response && error.response.data) {
                if (error.response.data.detail) {
                    errorMessage = error.response.data.detail;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                }
            } else if (error.message && error.message.includes("not found")) {
                errorMessage = "The video could not be found in the database.";
            }

            toast({
                variant: "destructive",
                title: "Analysis failed",
                description: errorMessage,
            });

            setIsAnalyzing(false);
        }
    };

    return (
        <AnalysisContext.Provider
            value={{
                selectedVideo,
                setSelectedVideo,
                isAnalyzing,
                setIsAnalyzing,
                analysisResults,
                setAnalysisResults,
                processingProgress,
                processingStatus,
                processingError,
                showVectors,
                setShowVectors,
                flowVectorsData,
                setFlowVectorsData,
                startAnalysis,
                cancelAnalysis,
            }}
        >
            {children}
        </AnalysisContext.Provider>
    );
};
